import React, { useMemo, useRef } from "react";
import { CSSTransition } from "react-transition-group";
import { StyledContainer, StyledContentContainer, COLLAPSIBLE_TRANSITION_DURATION_MS } from "./Collapsible.style";
const ifRefCurrent = (refContainer, refContentContainer, callback) => () => {
    if (refContainer.current && refContentContainer.current) {
        callback(refContainer.current, refContentContainer.current);
    }
};
const setAbsoluteContainerPosition = (contentContainer, animate) => {
    contentContainer.style.position = "absolute";
    contentContainer.style.top = animate === "bottom" ? "auto" : "0px";
    contentContainer.style.bottom = animate === "top" ? "auto" : "0px";
    contentContainer.style.left = animate === "right" ? "auto" : "0px";
    contentContainer.style.right = animate === "left" ? "auto" : "0px";
};
/**
 * Helper container used to animate collapsing/expanding content.
 *
 * Currently used when animating `TitleBar.expandableContent` and `Accordion`
 *
 * Uses [react-transition-group/css-transition](https://reactcommunity.org/react-transition-group/css-transition).
 *
 *
 */
const Collapsible = ({ collapsed, animate = "top", id, "data-insights": insightsAttr, children, }) => {
    const refContainer = useRef(null);
    const refContentContainer = useRef(null);
    const callbacks = useMemo(() => {
        const isHorizontalAnimation = ["left", "right"].includes(animate);
        const dimensionProp = isHorizontalAnimation ? "width" : "height";
        const dimensionComplementaryProp = isHorizontalAnimation ? "height" : "width";
        const scrollProp = { width: "scrollWidth", height: "scrollHeight" };
        return {
            onEnter: ifRefCurrent(refContainer, refContentContainer, (container, contentContainer) => {
                // reset container
                container.style["overflow"] = "hidden";
                container.style[dimensionProp] = "0px";
                container.removeAttribute("aria-hidden");
                // compute contentContainer complementary size, set to container before contentContainer becomes absolute
                container.style[dimensionComplementaryProp] = "100%";
                contentContainer.style[dimensionComplementaryProp] = "100%";
                container.style[dimensionComplementaryProp] = `${contentContainer[scrollProp[dimensionComplementaryProp]]}px`;
            }),
            onEntering: ifRefCurrent(refContainer, refContentContainer, (container, contentContainer) => {
                // contentContainer should "stick" to the animated part
                setAbsoluteContainerPosition(contentContainer, animate);
                // animate container width / height
                const scroll = scrollProp[dimensionProp];
                container.style[dimensionProp] = `${contentContainer[scroll]}px`;
            }),
            onEntered: ifRefCurrent(refContainer, refContentContainer, (container, contentContainer) => {
                // once animation is over content should be responsive, shadows should be visible
                contentContainer.style.position = "relative";
                container.style[dimensionProp] = "auto";
                container.style[dimensionComplementaryProp] = "100%";
                container.style["overflow"] = "visible";
            }),
            onExit: ifRefCurrent(refContainer, refContentContainer, (container, contentContainer) => {
                // prepare for collapse animation start
                container.style["overflow"] = "hidden";
                const scroll = scrollProp[dimensionProp];
                const scrollComplementary = scrollProp[dimensionComplementaryProp];
                container.style[dimensionProp] = `${contentContainer[scroll]}px`;
                container.style[dimensionComplementaryProp] = `${contentContainer[scrollComplementary]}px`;
            }),
            onExiting: ifRefCurrent(refContainer, refContentContainer, (container, contentContainer) => {
                // contentContainer should "stick" to the animated part
                setAbsoluteContainerPosition(contentContainer, animate);
                // collapse animation
                container.style[dimensionProp] = "0px";
            }),
            onExited: ifRefCurrent(refContainer, refContentContainer, (container) => {
                container.setAttribute("aria-hidden", "true");
                container.style[dimensionComplementaryProp] = `100%`;
            }),
        };
    }, [animate]);
    return (React.createElement("div", { id: id, "data-insights": insightsAttr, "aria-hidden": collapsed || undefined },
        React.createElement(CSSTransition, Object.assign({ appear: true, in: !collapsed, mountOnEnter: true, unmountOnExit: true, timeout: COLLAPSIBLE_TRANSITION_DURATION_MS }, callbacks),
            React.createElement(StyledContainer, { ref: refContainer, animate: animate },
                React.createElement(StyledContentContainer, { ref: refContentContainer, isCollapsed: collapsed }, children)))));
};
export default Collapsible;
//# sourceMappingURL=Collapsible.js.map